import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { caretPositionFromPoint, collapseToOneCharacterAtStart, getBoundingPageRect, getPageRects, splitRangeToTextNodes, supportsCaretPositionFromPoint } from "../../lib/range";
import ReactDOM from "react-dom";
import { IconNoteLarge } from "../../../../common/components/common/icons";
import { closestElement } from "../../lib/nodes";
import { isSafari } from "../../../../common/lib/utilities";
import { rectsEqual } from "../../lib/rect";
import cx from "classnames";
export const AnnotationOverlay = (props) => {
    let { iframe, annotations, selectedAnnotationIDs, onPointerDown, onPointerUp, onContextMenu, onDragStart, onResizeStart, onResizeEnd } = props;
    let [isResizing, setResizing] = useState(false);
    let [isPointerDownOutside, setPointerDownOutside] = useState(false);
    let [isAltDown, setAltDown] = useState(false);
    let pointerEventsSuppressed = isResizing || isPointerDownOutside || isAltDown;
    useEffect(() => {
        let win = iframe.contentWindow;
        if (!win) {
            return undefined;
        }
        let handleWindowPointerDown = (event) => {
            setAltDown(event.altKey);
            if (event.button == 0 && !event.target.closest('#annotation-overlay')) {
                setPointerDownOutside(true);
            }
        };
        let handleWindowPointerUp = (event) => {
            setAltDown(event.altKey);
            if (event.button == 0) {
                setPointerDownOutside(false);
            }
        };
        let handleWindowKeyDownCapture = (event) => {
            if (event.key == 'Alt') {
                setAltDown(true);
            }
        };
        let handleWindowKeyUpCapture = (event) => {
            if (event.key == 'Alt') {
                setAltDown(false);
            }
        };
        win.addEventListener('pointerdown', handleWindowPointerDown, { passive: true });
        win.addEventListener('pointerup', handleWindowPointerUp, { passive: true });
        // Listen for Alt on the iframe window and the root window, because the iframe window doesn't get the event
        // when an annotation text field is focused
        win.addEventListener('keydown', handleWindowKeyDownCapture, { capture: true, passive: true });
        win.addEventListener('keyup', handleWindowKeyUpCapture, { capture: true, passive: true });
        window.addEventListener('keydown', handleWindowKeyDownCapture, { capture: true, passive: true });
        window.addEventListener('keyup', handleWindowKeyUpCapture, { capture: true, passive: true });
        return () => {
            win.removeEventListener('pointerdown', handleWindowPointerDown);
            win.removeEventListener('pointerup', handleWindowPointerUp);
            win.removeEventListener('keydown', handleWindowKeyDownCapture, { capture: true });
            win.removeEventListener('keyup', handleWindowKeyUpCapture, { capture: true });
        };
    }, [iframe.contentWindow]);
    let handlePointerDown = useCallback((annotation, event) => {
        onPointerDown(annotation.id, event);
    }, [onPointerDown]);
    let handlePointerUp = useCallback((annotation, event) => {
        onPointerUp(annotation.id, event);
    }, [onPointerUp]);
    let handleContextMenu = useCallback((annotation, event) => {
        onContextMenu(annotation.id, event);
    }, [onContextMenu]);
    let handleDragStart = useCallback((annotation, dataTransfer) => {
        onDragStart(annotation.id, dataTransfer);
    }, [onDragStart]);
    let handleResizeStart = useCallback((annotation) => {
        setResizing(true);
        onResizeStart(annotation.id);
    }, [onResizeStart]);
    let handleResizeEnd = useCallback((annotation, range, cancelled) => {
        setResizing(false);
        onResizeEnd(annotation.id, range, cancelled);
    }, [onResizeEnd]);
    let widgetContainer = useRef(null);
    let highlightUnderlines = [];
    let numSelectedHighlightUnderlines = 0;
    let notes = [];
    let notePreviews = [];
    for (let annotation of annotations) {
        if (annotation.type === 'highlight' || annotation.type === 'underline') {
            // Put selected highlights/underlines at the end of the array,
            // so they render on top
            if (annotation.id && selectedAnnotationIDs.includes(annotation.id)) {
                highlightUnderlines.push(annotation);
                numSelectedHighlightUnderlines++;
            }
            else {
                highlightUnderlines.splice(highlightUnderlines.length - numSelectedHighlightUnderlines, 0, annotation);
            }
        }
        else if (annotation.type == 'note') {
            if (annotation.id) {
                notes.push(annotation);
            }
            else {
                notePreviews.push(annotation);
            }
        }
    }
    return React.createElement(React.Fragment, null,
        React.createElement("svg", { className: cx('annotation-container blended', { 'disable-pointer-events': pointerEventsSuppressed }) },
            highlightUnderlines.map((annotation) => {
                if (annotation.id) {
                    return (React.createElement(HighlightOrUnderline, { annotation: annotation, key: annotation.key, selected: selectedAnnotationIDs.includes(annotation.id), singleSelection: selectedAnnotationIDs.length == 1, onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onContextMenu: handleContextMenu, onDragStart: handleDragStart, onResizeStart: handleResizeStart, onResizeEnd: handleResizeEnd, widgetContainer: widgetContainer.current }));
                }
                else {
                    return (React.createElement("g", { className: "disable-pointer-events", key: annotation.key },
                        React.createElement(HighlightOrUnderline, { annotation: annotation, selected: false, singleSelection: false, widgetContainer: widgetContainer.current })));
                }
            }),
            notePreviews.map(annotation => (React.createElement(NotePreview, { annotation: annotation, key: annotation.key })))),
        React.createElement("svg", { className: cx('annotation-container', { 'disable-pointer-events': pointerEventsSuppressed }), ref: widgetContainer },
            React.createElement(StaggeredNotes, { annotations: notes, selectedAnnotationIDs: selectedAnnotationIDs, onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onContextMenu: handleContextMenu, onDragStart: handleDragStart })));
};
AnnotationOverlay.displayName = 'AnnotationOverlay';
let HighlightOrUnderline = (props) => {
    let { annotation, selected, singleSelection, onPointerDown, onPointerUp, onContextMenu, onDragStart, onResizeStart, onResizeEnd, widgetContainer } = props;
    let [isResizing, setResizing] = useState(false);
    let [resizedRange, setResizedRange] = useState(annotation.range);
    let outerGroupRef = useRef(null);
    let rectGroupRef = useRef(null);
    let dragImageRef = isSafari ? outerGroupRef : rectGroupRef;
    let handlePointerDown = useCallback((event) => {
        onPointerDown?.(annotation, event);
    }, [annotation, onPointerDown]);
    let handlePointerUp = useCallback((event) => {
        onPointerUp?.(annotation, event);
    }, [annotation, onPointerUp]);
    let handleContextMenu = useCallback((event) => {
        onContextMenu?.(annotation, event);
    }, [annotation, onContextMenu]);
    let handleDragStart = useCallback((event) => {
        if (!onDragStart || annotation.text === undefined) {
            return;
        }
        let elem = dragImageRef.current;
        if (elem) {
            let br = elem.getBoundingClientRect();
            event.dataTransfer.setDragImage(elem, event.clientX - br.left, event.clientY - br.top);
        }
        onDragStart(annotation, event.dataTransfer);
    }, [annotation, dragImageRef, onDragStart]);
    let handleResizeStart = useCallback((annotation) => {
        setResizing(true);
        setResizedRange(annotation.range);
        onResizeStart?.(annotation);
    }, [onResizeStart]);
    let handleResizeEnd = useCallback((annotation, cancelled) => {
        setResizing(false);
        onResizeEnd?.(annotation, resizedRange, cancelled);
    }, [onResizeEnd, resizedRange]);
    let handleResize = useCallback((annotation, range) => {
        setResizedRange(range);
    }, []);
    let allowResize = selected && singleSelection && !annotation.readOnly && supportsCaretPositionFromPoint();
    useEffect(() => {
        if (!allowResize && isResizing) {
            handleResizeEnd(annotation, true);
        }
    }, [allowResize, annotation, handleResizeEnd, isResizing]);
    let { rects, interactiveRects, commentIconPosition } = useMemo(() => {
        let ranges = splitRangeToTextNodes(isResizing ? resizedRange : annotation.range);
        let rects = new Map();
        let interactiveRects = new Set();
        for (let range of ranges) {
            let closestInteractiveElement = range.startContainer.parentElement?.closest('a, area');
            for (let rect of getPageRects(range)) {
                if (rect.width == 0 || rect.height == 0) {
                    continue;
                }
                let key = JSON.stringify(rect);
                if (!rects.has(key)) {
                    rects.set(key, rect);
                    if (closestInteractiveElement) {
                        interactiveRects.add(rect);
                    }
                }
            }
        }
        let commentIconPosition;
        if (annotation.comment) {
            let commentIconRange = ranges[0].cloneRange();
            collapseToOneCharacterAtStart(commentIconRange);
            let rect = getBoundingPageRect(commentIconRange);
            commentIconPosition = { x: rect.x, y: rect.y };
        }
        else {
            commentIconPosition = null;
        }
        return { rects, interactiveRects, commentIconPosition };
    }, [annotation, isResizing, resizedRange]);
    let rectGroup = useMemo(() => {
        return React.createElement("g", { ref: rectGroupRef }, [...rects.entries()].map(([key, rect]) => (React.createElement("rect", { x: rect.x, y: annotation.type == 'underline' ? rect.y + rect.height : rect.y, width: rect.width, height: annotation.type == 'underline' ? 3 : rect.height, opacity: "50%", key: key }))));
    }, [annotation, rects]);
    let foreignObjects = useMemo(() => {
        return !isResizing && [...rects.entries()].map(([key, rect]) => (
        // Yes, this is horrible, but SVGs don't support drag events without embedding HTML in a <foreignObject>
        React.createElement("foreignObject", { x: rect.x, y: rect.y, width: rect.width, height: rect.height, className: "needs-pointer-events", key: key + '-foreign' },
            React.createElement("div", { 
                // @ts-ignore
                xmlns: "http://www.w3.org/1999/xhtml", className: cx('annotation-div', { 'disable-pointer-events': interactiveRects.has(rect) }), draggable: true, onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onContextMenu: handleContextMenu, onDragStart: handleDragStart, "data-annotation-id": annotation.id }))));
    }, [annotation, handleContextMenu, handleDragStart, handlePointerDown, handlePointerUp, interactiveRects, isResizing, rects]);
    let resizer = useMemo(() => {
        return allowResize && (React.createElement(Resizer, { annotation: annotation, highlightRects: [...rects.values()], onResizeStart: handleResizeStart, onResizeEnd: handleResizeEnd, onResize: handleResize }));
    }, [allowResize, annotation, handleResize, handleResizeEnd, handleResizeStart, rects]);
    if (!rects.size) {
        return null;
    }
    // When the user drags the annotation, we *want* to set the drag image to the rendered annotation -- a highlight
    // rectangle for a highlight annotation, an underline for an underline annotation. We don't want to include
    // resizers. But in Safari, passing an SVG sub-element to setDragImage() doesn't actually set the drag image to the
    // rendered content of that element, but rather to all the text contained within its bounding box (but not
    // necessarily within the element itself in the DOM tree). This is very weird and means that underline annotations
    // will have a blank drag image because the underline doesn't overlap any text. So when running in Safari, we pass
    // the whole outer <g> containing the underline/highlight (potentially small) and the interactive <foreignObject>s
    // (big) so that we get all the highlighted text to render in the drag image.
    return React.createElement(React.Fragment, null,
        React.createElement("g", { fill: annotation.color, ref: outerGroupRef },
            rectGroup,
            foreignObjects,
            resizer),
        widgetContainer && ((selected && !isResizing) || commentIconPosition) && ReactDOM.createPortal(React.createElement(React.Fragment, null,
            selected && !isResizing && (React.createElement(SelectionBorder, { rect: getBoundingPageRect(annotation.range) })),
            commentIconPosition && (React.createElement(CommentIcon, { ...commentIconPosition, color: annotation.color }))), widgetContainer));
};
HighlightOrUnderline.displayName = 'HighlightOrUnderline';
HighlightOrUnderline = memo(HighlightOrUnderline);
const Note = (props) => {
    let { annotation, staggerIndex, selected, onPointerDown, onPointerUp, onContextMenu, onDragStart } = props;
    let dragImageRef = useRef(null);
    let doc = annotation.range.commonAncestorContainer.ownerDocument;
    let handleDragStart = useCallback((event) => {
        if (!onDragStart || annotation.comment === undefined) {
            return;
        }
        let elem = dragImageRef.current;
        if (elem) {
            let br = elem.getBoundingClientRect();
            event.dataTransfer.setDragImage(elem, event.clientX - br.left, event.clientY - br.top);
        }
        onDragStart(annotation, event.dataTransfer);
    }, [annotation, onDragStart]);
    if (!doc || !doc.defaultView) {
        return null;
    }
    let rect = annotation.range.getBoundingClientRect();
    rect.x += doc.defaultView.scrollX;
    rect.y += doc.defaultView.scrollY;
    let rtl = getComputedStyle(closestElement(annotation.range.commonAncestorContainer)).direction === 'rtl';
    let staggerOffset = (staggerIndex || 0) * 15;
    let x = rect.left + (rtl ? -25 : rect.width + 25) + (rtl ? -1 : 1) * staggerOffset;
    let y = rect.top + staggerOffset;
    return (React.createElement(CommentIcon, { ref: dragImageRef, annotation: annotation, x: x, y: y, color: annotation.color, opacity: annotation.id ? '100%' : '50%', selected: selected, large: true, onPointerDown: onPointerDown && (event => onPointerDown(annotation, event)), onPointerUp: onPointerUp && (event => onPointerUp(annotation, event)), onContextMenu: onContextMenu && (event => onContextMenu(annotation, event)), onDragStart: handleDragStart }));
};
Note.displayName = 'Note';
let NotePreview = (props) => {
    let { annotation } = props;
    let doc = annotation.range.commonAncestorContainer.ownerDocument;
    if (!doc || !doc.defaultView) {
        return null;
    }
    let rect = annotation.range.getBoundingClientRect();
    rect.x += doc.defaultView.scrollX;
    rect.y += doc.defaultView.scrollY;
    return React.createElement(SelectionBorder, { rect: rect, preview: true, key: annotation.key });
};
NotePreview.displayName = 'NotePreview';
NotePreview = memo(NotePreview);
const StaggeredNotes = (props) => {
    let { annotations, selectedAnnotationIDs, onPointerDown, onPointerUp, onContextMenu, onDragStart } = props;
    let staggerMap = new Map();
    return React.createElement(React.Fragment, null, annotations.map((annotation) => {
        let stagger = staggerMap.has(annotation.sortIndex) ? staggerMap.get(annotation.sortIndex) : 0;
        staggerMap.set(annotation.sortIndex, stagger + 1);
        if (annotation.id) {
            return (React.createElement(Note, { annotation: annotation, staggerIndex: stagger, key: annotation.key, selected: selectedAnnotationIDs.includes(annotation.id), onPointerDown: onPointerDown, onPointerUp: onPointerUp, onContextMenu: onContextMenu, onDragStart: onDragStart }));
        }
        else {
            return (React.createElement("div", { className: "disable-pointer-events", key: annotation.key },
                React.createElement(Note, { annotation: annotation, staggerIndex: stagger, key: annotation.key, selected: false })));
        }
    }));
};
StaggeredNotes.displayName = 'StaggeredNotes';
let SelectionBorder = (props) => {
    let { rect, preview } = props;
    return (React.createElement("rect", { x: rect.left - 5, y: rect.top - 5, width: rect.width + 10, height: rect.height + 10, fill: "none", stroke: preview ? '#aaaaaa' : '#6d95e0', strokeDasharray: "10 6", strokeWidth: 2 }));
};
SelectionBorder.displayName = 'SelectionBorder';
SelectionBorder = memo(SelectionBorder, (prev, next) => {
    return rectsEqual(prev.rect, next.rect) && prev.preview === next.preview;
});
const Resizer = (props) => {
    let WIDTH = 3;
    let { annotation, highlightRects, onResize, onResizeEnd, onResizeStart } = props;
    let [resizingSide, setResizingSide] = useState(false);
    let [pointerCapture, setPointerCapture] = useState(null);
    let rtl = getComputedStyle(closestElement(annotation.range.commonAncestorContainer)).direction == 'rtl';
    highlightRects = Array.from(highlightRects)
        .sort((a, b) => (a.bottom - b.bottom) || (a.left - b.left));
    let handlePointerDown = useCallback((event) => {
        if (event.button !== 0) {
            return;
        }
        event.preventDefault();
        event.target.setPointerCapture(event.pointerId);
    }, []);
    let handlePointerUp = useCallback((event) => {
        if (event.button !== 0
            || !resizingSide
            || !event.target.hasPointerCapture(event.pointerId)) {
            return;
        }
        event.target.releasePointerCapture(event.pointerId);
    }, [resizingSide]);
    let handleGotPointerCapture = useCallback((event, side) => {
        setResizingSide(side);
        setPointerCapture({ elem: event.target, pointerId: event.pointerId });
        onResizeStart(annotation);
    }, [annotation, onResizeStart]);
    let handleLostPointerCapture = useCallback(() => {
        setResizingSide(false);
        if (pointerCapture) {
            setPointerCapture(null);
            onResizeEnd(annotation, false);
        }
    }, [annotation, onResizeEnd, pointerCapture]);
    let handleKeyDown = useCallback((event) => {
        if (event.key !== 'Escape' || !resizingSide || !pointerCapture) {
            return;
        }
        pointerCapture.elem.releasePointerCapture(pointerCapture.pointerId);
        setResizingSide(false);
        setPointerCapture(null);
        onResizeEnd(annotation, true);
    }, [pointerCapture, onResizeEnd, annotation, resizingSide]);
    let doc = annotation.range.commonAncestorContainer.ownerDocument;
    let win = doc?.defaultView;
    useEffect(() => {
        if (!win) {
            return undefined;
        }
        win.addEventListener('keydown', handleKeyDown, true);
        return () => win?.removeEventListener('keydown', handleKeyDown, true);
    }, [win, handleKeyDown]);
    let handlePointerMove = useCallback((event, isStart) => {
        let clientX = event.clientX;
        if (isSafari) {
            let targetRect = event.target.getBoundingClientRect();
            if (clientX >= targetRect.left && clientX <= targetRect.right) {
                // In Safari, caretPositionFromPoint() doesn't work if the mouse is directly over the target element
                // (returns the last element in the body instead), so we have to offset the X position by 1 pixel.
                // This makes resizing a bit jerkier, but it's better than the alternative.
                clientX = isStart ? targetRect.left - 1 : targetRect.right + 1;
            }
        }
        let pos = caretPositionFromPoint(event.view.document, clientX, event.clientY);
        if (pos) {
            // Just bail if the browser thinks the mouse is over the SVG - that seems to only happen momentarily
            if (pos.offsetNode.nodeType == Node.ELEMENT_NODE && pos.offsetNode.closest('svg')) {
                return;
            }
            let relativePosition = annotation.range.comparePoint(pos.offsetNode, pos.offset);
            let newRange = annotation.range.cloneRange();
            if (isStart) {
                if (relativePosition <= 0) {
                    newRange.setStart(pos.offsetNode, pos.offset);
                }
                else {
                    // Resizing the start past the end - swap the two
                    newRange.setStart(newRange.endContainer, newRange.endOffset);
                    newRange.setEnd(pos.offsetNode, pos.offset);
                }
            }
            else {
                // eslint-disable-next-line no-lonely-if
                if (relativePosition >= 0) {
                    newRange.setEnd(pos.offsetNode, pos.offset);
                }
                else {
                    // Resizing the end past the start - swap the two
                    newRange.setEnd(newRange.startContainer, newRange.startOffset);
                    newRange.setStart(pos.offsetNode, pos.offset);
                }
            }
            if (newRange.collapsed
                || !newRange.toString().trim().length
                || newRange.getClientRects().length == 0
                // Make sure we stay within one section
                || doc?.querySelector('[data-section-index]')
                    && !closestElement(newRange.commonAncestorContainer)?.closest('[data-section-index]')) {
                return;
            }
            let boundingRect = newRange.getBoundingClientRect();
            if (!boundingRect.width || !boundingRect.height) {
                return;
            }
            onResize(annotation, newRange);
        }
    }, [annotation, doc, onResize]);
    if (!highlightRects.length) {
        return null;
    }
    let topLeftRect = highlightRects[rtl ? highlightRects.length - 1 : 0];
    let bottomRightRect = highlightRects[rtl ? 0 : highlightRects.length - 1];
    return React.createElement(React.Fragment, null,
        React.createElement("rect", { x: topLeftRect.left - WIDTH, y: topLeftRect.top, width: WIDTH, height: topLeftRect.height, fill: annotation.color, className: "resizer inherit-pointer-events", onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onPointerCancel: handlePointerUp, onPointerMove: resizingSide == 'start' ? (event => handlePointerMove(event, !rtl)) : undefined, onGotPointerCapture: event => handleGotPointerCapture(event, 'start'), onLostPointerCapture: handleLostPointerCapture }),
        React.createElement("rect", { x: bottomRightRect.right, y: bottomRightRect.top, width: WIDTH, height: bottomRightRect.height, fill: annotation.color, className: "resizer inherit-pointer-events", onPointerDown: handlePointerDown, onPointerUp: handlePointerUp, onPointerCancel: handlePointerUp, onPointerMove: resizingSide == 'end' ? (event => handlePointerMove(event, rtl)) : undefined, onGotPointerCapture: event => handleGotPointerCapture(event, 'end'), onLostPointerCapture: handleLostPointerCapture }));
};
Resizer.displayName = 'Resizer';
let CommentIcon = React.forwardRef((props, ref) => {
    let size = props.large ? 24 : 14;
    let x = props.x - size / 2;
    let y = props.y - size / 2;
    return React.createElement(React.Fragment, null,
        React.createElement("svg", { color: props.color, opacity: props.opacity, x: x, y: y, width: size, height: size, viewBox: "0 0 24 24", ref: ref },
            React.createElement(IconNoteLarge, null)),
        props.selected && (React.createElement(SelectionBorder, { rect: new DOMRect(x, y, size, size) })),
        React.createElement("foreignObject", { x: x, y: y, width: size, height: size, className: "needs-pointer-events" },
            React.createElement("div", { 
                // @ts-ignore
                xmlns: "http://www.w3.org/1999/xhtml", className: "annotation-div", draggable: true, onPointerDown: props.onPointerDown, onPointerUp: props.onPointerUp, onContextMenu: props.onContextMenu, onDragStart: props.onDragStart, onDragEnd: props.onDragEnd, "data-annotation-id": props.annotation?.id })));
});
CommentIcon.displayName = 'CommentIcon';
CommentIcon = memo(CommentIcon);
